import { CHUNK_SIZE } from '../core/world';

/**
 * Local connectivity helpers for the 8×8 chunk grid. Axial 6-neighbour
 * adjacency; neighbours outside the chunk are not considered here — cross-
 * chunk connectivity is enforced by the border handshake, not by carving.
 *
 * Grids are flat typed arrays of length `CHUNK_SIZE * CHUNK_SIZE`,
 * indexed `lr * CHUNK_SIZE + lq` (row-major in r, matching `Chunk.tiles`).
 */

const GRID_LEN = CHUNK_SIZE * CHUNK_SIZE;

const LOCAL_NEIGHBOUR_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export function localIdx(lq: number, lr: number): number {
  return lr * CHUNK_SIZE + lq;
}

export function inBounds(lq: number, lr: number): boolean {
  return lq >= 0 && lq < CHUNK_SIZE && lr >= 0 && lr < CHUNK_SIZE;
}

/**
 * Flood-fill passable cells into connected components. Returns an Int32Array
 * of length CHUNK_SIZE² with one label per cell; -1 marks impassable cells.
 * Component labels are stable: cells are scanned in row-major (lr outer, lq
 * inner) order and labels assigned in the order each new component is first
 * encountered.
 */
export function components(passable: Uint8Array): Int32Array {
  const labels = new Int32Array(GRID_LEN).fill(-1);
  let next = 0;
  for (let lr = 0; lr < CHUNK_SIZE; lr++) {
    for (let lq = 0; lq < CHUNK_SIZE; lq++) {
      const idx = localIdx(lq, lr);
      if (passable[idx] === 0 || labels[idx] !== -1) continue;
      labels[idx] = next;
      const stack: number[] = [lq, lr];
      while (stack.length > 0) {
        const cr = stack.pop() as number;
        const cq = stack.pop() as number;
        for (const [dq, dr] of LOCAL_NEIGHBOUR_OFFSETS) {
          const nq = cq + dq;
          const nr = cr + dr;
          if (!inBounds(nq, nr)) continue;
          const ni = localIdx(nq, nr);
          if (passable[ni] === 1 && labels[ni] === -1) {
            labels[ni] = next;
            stack.push(nq, nr);
          }
        }
      }
      next++;
    }
  }
  return labels;
}

/** BFS from (startLq, startLr) until `isTarget` returns true. Returns the
 * shortest path as a flat array of alternating `lq, lr` pairs, or `null`
 * if no path exists. Walks all cells regardless of passability — caller
 * flips impassable cells along the path to passable. */
function bfsTo(
  startLq: number,
  startLr: number,
  isTarget: (lq: number, lr: number) => boolean,
): number[] | null {
  const visited = new Uint8Array(GRID_LEN);
  const prev = new Int32Array(GRID_LEN).fill(-1);
  visited[localIdx(startLq, startLr)] = 1;
  const queue: number[] = [startLq, startLr];
  let head = 0;
  let endIdx = -1;
  while (head < queue.length) {
    const cq = queue[head++] as number;
    const cr = queue[head++] as number;
    if (isTarget(cq, cr)) {
      endIdx = localIdx(cq, cr);
      break;
    }
    for (const [dq, dr] of LOCAL_NEIGHBOUR_OFFSETS) {
      const nq = cq + dq;
      const nr = cr + dr;
      if (!inBounds(nq, nr)) continue;
      const ni = localIdx(nq, nr);
      if (visited[ni] === 1) continue;
      visited[ni] = 1;
      prev[ni] = localIdx(cq, cr);
      queue.push(nq, nr);
    }
  }
  if (endIdx === -1) return null;
  const path: number[] = [];
  let cur = endIdx;
  for (;;) {
    const lr = (cur / CHUNK_SIZE) | 0;
    const lq = cur - lr * CHUNK_SIZE;
    path.push(lq, lr);
    if (cur === localIdx(startLq, startLr)) break;
    const p = prev[cur] as number;
    if (p === -1) break;
    cur = p;
  }
  // Path is returned in walk-back order (end → start). Carving doesn't
  // care about order — every cell on the path is flipped to passable —
  // and reversing a flat `[lq, lr, lq, lr, …]` array swaps pair elements,
  // which would carve the wrong cells.
  return path;
}

/**
 * Unify all passable cells (and every cell in `forced`) into a single
 * connected component by carving impassable→passable along BFS-shortest
 * paths from outlier components to the largest one.
 *
 * `forced` cells are assumed already passable (the caller's responsibility);
 * they will be guaranteed reachable from the unified component on return.
 */
export function unifyConnectivity(
  passable: Uint8Array,
  forced: ReadonlyArray<readonly [number, number]>,
): void {
  // Worst case: every cell is its own component. Conservative loop bound.
  for (let iter = 0; iter < GRID_LEN; iter++) {
    const labels = components(passable);
    const sizes = new Map<number, number>();
    for (let i = 0; i < GRID_LEN; i++) {
      const l = labels[i] as number;
      if (l === -1) continue;
      sizes.set(l, (sizes.get(l) ?? 0) + 1);
    }
    if (sizes.size === 0) {
      // Degenerate: no passable cells at all. Bootstrap at chunk centre.
      const mid = Math.floor(CHUNK_SIZE / 2);
      passable[localIdx(mid, mid)] = 1;
      continue;
    }
    let largestLabel = -1;
    let largestSize = -1;
    for (const [label, size] of sizes) {
      if (size > largestSize || (size === largestSize && label < largestLabel)) {
        largestSize = size;
        largestLabel = label;
      }
    }
    // Find a non-largest cell to connect. Prioritise forced cells so the
    // border anchors all end up in the unified component.
    let targetLq = -1;
    let targetLr = -1;
    for (const cell of forced) {
      const fq = cell[0];
      const fr = cell[1];
      if (labels[localIdx(fq, fr)] !== largestLabel) {
        targetLq = fq;
        targetLr = fr;
        break;
      }
    }
    if (targetLq === -1) {
      outer: for (let lr = 0; lr < CHUNK_SIZE; lr++) {
        for (let lq = 0; lq < CHUNK_SIZE; lq++) {
          const i = localIdx(lq, lr);
          if (passable[i] === 1 && labels[i] !== largestLabel) {
            targetLq = lq;
            targetLr = lr;
            break outer;
          }
        }
      }
    }
    if (targetLq === -1) return;
    const path = bfsTo(targetLq, targetLr, (q, r) => labels[localIdx(q, r)] === largestLabel);
    if (!path) return;
    for (let i = 0; i < path.length; i += 2) {
      passable[localIdx(path[i] as number, path[i + 1] as number)] = 1;
    }
  }
}
